/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

/**
 * The 17 FIT base types.
 *
 * [id] is the byte written into a field definition record; bit 7 flags a
 * multi-byte type whose endianness follows the message definition.
 *
 * FIT has no null. Every base type reserves one value as its *invalid*
 * sentinel, which the SDK surfaces as `null`.
 */
public enum class BaseType(
    public val id: UByte,
    public val size: Int,
) {
    ENUM(0x00u, 1),
    SINT8(0x01u, 1),
    UINT8(0x02u, 1),
    SINT16(0x83u, 2),
    UINT16(0x84u, 2),
    SINT32(0x85u, 4),
    UINT32(0x86u, 4),
    STRING(0x07u, 1),
    FLOAT32(0x88u, 4),
    FLOAT64(0x89u, 8),
    UINT8Z(0x0Au, 1),
    UINT16Z(0x8Bu, 2),
    UINT32Z(0x8Cu, 4),
    BYTE(0x0Du, 1),
    SINT64(0x8Eu, 8),
    UINT64(0x8Fu, 8),
    UINT64Z(0x90u, 8),
    ;

    public val isNumeric: Boolean get() = this != ENUM && this != STRING

    public val isFloatingPoint: Boolean get() = this == FLOAT32 || this == FLOAT64

    public val isSignedInteger: Boolean
        get() = this == SINT8 || this == SINT16 || this == SINT32 || this == SINT64

    /**
     * The value this SDK surfaces as "absent".
     *
     * For the float types this is the all-ones bit pattern, *not* [Float.NaN].
     * Both are quiet NaNs, but only the former survives a decode/encode round
     * trip: writing [Float.NaN] back out would emit 0x7FC00000 where the file
     * held 0xFFFFFFFF.
     */
    public fun invalidValue(): Any =
        when (this) {
            ENUM, UINT8, BYTE -> UByte.MAX_VALUE
            SINT8 -> Byte.MAX_VALUE
            SINT16 -> Short.MAX_VALUE
            UINT16 -> UShort.MAX_VALUE
            SINT32 -> Int.MAX_VALUE
            UINT32 -> UInt.MAX_VALUE
            STRING -> ""
            FLOAT32 -> Float.fromBits(-1)
            FLOAT64 -> Double.fromBits(-1L)
            UINT8Z -> UByte.MIN_VALUE
            UINT16Z -> UShort.MIN_VALUE
            UINT32Z -> UInt.MIN_VALUE
            SINT64 -> Long.MAX_VALUE
            UINT64 -> ULong.MAX_VALUE
            UINT64Z -> ULong.MIN_VALUE
        }

    /** The bytes written for an absent value of this type, little-endian. */
    public fun invalidBytes(): ByteArray =
        when (this) {
            STRING -> byteArrayOf(0x00)
            UINT8Z -> byteArrayOf(0x00)
            UINT16Z -> ByteArray(2)
            UINT32Z -> ByteArray(4)
            UINT64Z -> ByteArray(8)
            SINT8 -> byteArrayOf(0x7F)
            SINT16 -> byteArrayOf(0xFF.toByte(), 0x7F)
            SINT32 -> byteArrayOf(-1, -1, -1, 0x7F)
            SINT64 -> byteArrayOf(-1, -1, -1, -1, -1, -1, -1, 0x7F)
            else -> ByteArray(size) { -1 }
        }

    /**
     * The invalid sentinel as a plain number, for comparing against a value
     * that came out of a bit stream rather than off the wire.
     *
     * For the float types this is the all-ones pattern read as an integer, not
     * a NaN: component expansion yields numbers, and an all-ones component is
     * how FIT says "no value" there too.
     */
    internal fun invalidAsNumber(): Double =
        when (this) {
            ENUM, UINT8, BYTE -> 255.0
            SINT8 -> 127.0
            SINT16 -> 32767.0
            UINT16 -> 65535.0
            SINT32 -> 2147483647.0
            UINT32, FLOAT32 -> 4294967295.0
            FLOAT64 -> 18446744073709551615.0
            SINT64 -> 9223372036854775807.0
            UINT64 -> 18446744073709551615.0
            UINT8Z, UINT16Z, UINT32Z, UINT64Z -> 0.0
            STRING -> Double.NaN
        }

    public fun isValid(value: Any?): Boolean {
        if (value == null) return false
        return when (this) {
            STRING -> {
                val text = FitValues.toStringValue(value).trimEnd('\u0000')
                text.encodeToByteArray().size <= Fit.STRING_MAX_BYTE_COUNT && text.isNotEmpty()
            }
            // Any NaN reads as invalid, which covers both 0xFFFFFFFF and a
            // genuine NaN a producer may have written.
            FLOAT32 -> !FitValues.toDouble(value).isNaN()
            FLOAT64 -> !FitValues.toDouble(value).isNaN()
            else -> correctRangeAndType(value) != invalidValue()
        }
    }

    public fun isInvalid(value: Any?): Boolean = !isValid(value)

    private val minValue: Double?
        get() = when (this) {
            STRING -> null
            SINT8 -> Byte.MIN_VALUE.toDouble()
            SINT16 -> Short.MIN_VALUE.toDouble()
            SINT32 -> Int.MIN_VALUE.toDouble()
            SINT64 -> Long.MIN_VALUE.toDouble()
            FLOAT32 -> -Float.MAX_VALUE.toDouble()
            FLOAT64 -> -Double.MAX_VALUE
            else -> 0.0
        }

    private val maxValue: Double?
        get() = when (this) {
            STRING -> null
            ENUM, UINT8, BYTE, UINT8Z -> UByte.MAX_VALUE.toDouble()
            SINT8 -> Byte.MAX_VALUE.toDouble()
            UINT16, UINT16Z -> UShort.MAX_VALUE.toDouble()
            SINT16 -> Short.MAX_VALUE.toDouble()
            UINT32, UINT32Z -> UInt.MAX_VALUE.toDouble()
            SINT32 -> Int.MAX_VALUE.toDouble()
            UINT64, UINT64Z -> ULong.MAX_VALUE.toDouble()
            SINT64 -> Long.MAX_VALUE.toDouble()
            FLOAT32 -> Float.MAX_VALUE.toDouble()
            FLOAT64 -> Double.MAX_VALUE
        }

    /**
     * Boxes [value] as this base type's Kotlin type, clamping anything out of
     * range onto the invalid sentinel.
     */
    public fun correctRangeAndType(value: Any?): Any? {
        if (this == STRING) return FitValues.toStringValue(value)

        val d = FitValues.toDouble(value)
        val min = minValue
        val max = maxValue
        if (min != null && max != null) {
            if (d.isNaN() || d.isInfinite() || d < min || d > max) return invalidValue()
        }

        // Integral types round rather than truncate. Component expansion divides
        // by a scale, so a value that should land exactly on 4087 arrives as
        // 4086.99999...; truncating there silently loses a unit.
        val isFloating = value is Double || value is Float
        val r = kotlin.math.round(d)

        // 64-bit integers do not survive a trip through Double, so take them
        // straight from the boxed value unless they arrived as a float.
        return when (this) {
            ENUM, UINT8, BYTE, UINT8Z -> r.toInt().toUByte()
            SINT8 -> r.toInt().toByte()
            SINT16 -> r.toInt().toShort()
            UINT16, UINT16Z -> r.toInt().toUShort()
            SINT32 -> r.toLong().toInt()
            UINT32, UINT32Z -> r.toLong().toUInt()
            SINT64 -> if (isFloating) r.toLong() else FitValues.toLong(value)
            UINT64, UINT64Z -> if (isFloating) r.toLong().toULong() else FitValues.toLong(value).toULong()
            FLOAT32 -> d.toFloat()
            FLOAT64 -> d
            STRING -> FitValues.toStringValue(value)
        }
    }

    public companion object {
        private val byId = entries.associateBy { it.id }

        /** The base type a field definition record's type byte denotes, if any. */
        public fun fromId(id: UByte): BaseType? = byId[id]

        /**
         * The base type that best represents an already-boxed Kotlin value.
         *
         * Kotlin/JS represents `Byte`, `Short`, `Int`, `Float` and `Double`
         * alike, as JS numbers, so on that target each of their branches below
         * matches any of the five and the first one decides. They are therefore
         * ordered widest first: an ambiguous value is held losslessly as a
         * `float64` rather than truncated into a byte. JVM and Wasm carry the
         * declared type at runtime and keep picking it exactly. Unsigned types,
         * `Long` and `String` are unambiguous everywhere.
         */
        public fun of(value: Any?): BaseType? =
            when (value) {
                is UByte, is Boolean -> UINT8
                is UShort -> UINT16
                is UInt -> UINT32
                is ULong -> UINT64
                is Long -> SINT64
                is Double -> FLOAT64
                is Float -> FLOAT32
                is Int -> SINT32
                is Short -> SINT16
                is Byte -> SINT8
                is String -> STRING
                else -> null
            }
    }
}
